/**
 * @file Renders an investor's name cell with note and warning indicators, optionally opening the investment drawer.
 */

import React, { useMemo } from "react";
import { Space, Tooltip, Typography } from "antd";
import { FileTextOutlined, WarningOutlined } from "@ant-design/icons";
import type { ReactNode } from "react";

import { W0 } from "@components/Constants";

import hasTin from "./helpers/hasTin";
import isTrustInvestment from "./helpers/isTrustInvestment";
import isSdiraInvestment from "./helpers/isSdiraInvestment";
import isNonUsPersonInvestment from "./helpers/isNonUsPersonInvestment";
import computeNonUsPersonWarning from "./helpers/computeNonUsPersonWarning";
import type { Investment } from "./helpers/types";

const { Link: TextLink } = Typography;

const LABEL_NO_TIN = "Doesn't have EIN / SSN.";
const LABEL_OTHER_CUSTODIAN = "Other custodian selected.";

const OTHER_CUSTODIAN_ID = "OTHER";

const ICON_SPACE_SIZE = W0 / 2;

type ColumnInvestment = Investment & {
  note?: string;
};

type Props = {
  name: ReactNode;
  isBold?: boolean;
  investment: ColumnInvestment;
  openInvestmentDrawer?: (investment: ColumnInvestment) => void;
};

/**
 * Renders the investor name with note and warning icons, linking to the investment drawer when one is given.
 */
const InvestorColumn = ({
  isBold = false,
  name,
  investment,
  openInvestmentDrawer,
}: Props): JSX.Element => {
  const warnings = useMemo((): string[] => {
    const { profileSdira = {} } = investment;

    const { custodianId } = profileSdira;

    const isNonUsPerson = isNonUsPersonInvestment(investment);

    const isSdira = isSdiraInvestment(investment);
    const isTrust = isTrustInvestment(investment);

    const hasNoTin = !hasTin(investment);

    const isTinRequired = !isSdira && !isTrust;

    const isTinMissing = isTinRequired && hasNoTin;

    // NOTE: the SDIRA profile object survives a profile type change, so
    //       the custodian warning applies only to a current SDIRA profile.
    const isCustodianOther = isSdira && custodianId === OTHER_CUSTODIAN_ID;

    const warningsList: string[] = [];

    if (isNonUsPerson) {
      const nonUsPersonWarning = computeNonUsPersonWarning(investment);

      warningsList.push(nonUsPersonWarning);
    }

    if (isCustodianOther) {
      warningsList.push(LABEL_OTHER_CUSTODIAN);
    }

    if (isTinMissing) {
      warningsList.push(LABEL_NO_TIN);
    }

    return warningsList;
  }, [ investment ]);

  const { note } = investment;

  const hasNote = !!note;
  const hasWarning = warnings.length > 0;

  // Each warning is independent, so each one gets its own line.
  const warningTitle = warnings.map((warning) => (
    <div key={warning}>
      {warning}
    </div>
  ));

  const content = (
    <Space size={ICON_SPACE_SIZE}>
      {
        hasNote && (
          <Tooltip
            title={note}
            placement="top"
          >
            <FileTextOutlined />
          </Tooltip>
        )
      }

      {
        hasWarning && (
          <Tooltip
            title={warningTitle}
            placement="top"
          >
            <WarningOutlined />
          </Tooltip>
        )
      }

      {
        isBold
          ? <strong>{name}</strong>
          : name
      }
    </Space>
  );

  const isStatic = !openInvestmentDrawer;

  if (isStatic) {
    return content;
  }

  /**
   * Opens the investment drawer for this row.
   */
  const onClick = (): void => openInvestmentDrawer(investment);

  return (
    <TextLink
      strong
      onClick={onClick}
    >
      {content}
    </TextLink>
  );
};

export default InvestorColumn;
